import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, CircleNotch, CheckCircle, Warning, FileText } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

export default function SignAgreement() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [readAll, setReadAll] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    publicApi.get(`/agreements/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const sign = async () => {
    if (!typedName.trim()) { toast.error("Please type your full legal name"); return; }
    if (!readAll) { toast.error("You must confirm you have read and accepted all clauses"); return; }
    setSaving(true);
    try {
      await publicApi.post(`/agreements/${token}/sign`, { typed_name: typedName, accept_all_clauses: readAll });
      setJustSigned(true);
      toast.success("Agreement signed — thank you");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signing failed");
    } finally { setSaving(false); }
  };

  if (loading) return <Center>Loading agreement…</Center>;
  if (invalid || !data) return <ScreenMsg icon="warn" title="Agreement not found" body="This signing link is invalid or has been revoked. Please contact SoverenX Systems." />;
  if (data.status === "signed" || justSigned) {
    return (
      <ScreenMsg
        icon="ok"
        title="Signed &amp; recorded"
        body={
          `Thank you${data.signature?.name ? ", " + data.signature.name : ""}. Your Contractor Agreement has been executed and a copy retained by SoverenX Systems. `+
          `A confirmation email has been sent to your inbox. Your personalised Offer Letter with rate and weekly hours will follow shortly.`
        }
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38]" data-testid="sign-agreement-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <header className="mb-8 pb-6 border-b border-slate-200">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
              <FileText size={17} className="text-[#B7791F]" weight="bold" />
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.24em] text-slate-500">SoverenX · Contractor Agreement</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Please review &amp; sign your engagement contract</h1>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl leading-relaxed">
            Read the document below carefully. It covers governing law (Australian courts), IP assignment, tax responsibilities in India, and confirms your status as an independent contractor. Sign digitally by typing your full legal name at the bottom.
          </p>
        </header>

        <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm mb-6" data-testid="agreement-body">
          <div dangerouslySetInnerHTML={{ __html: data.html }} />
        </div>

        <div className="bg-white border-2 border-[#FFC107] rounded-lg p-6 shadow-sm">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-4">Digital Signature</div>

          <label className="flex items-start gap-3 cursor-pointer p-3 bg-slate-50 border border-slate-200 rounded mb-4">
            <input data-testid="read-all-check" type="checkbox" checked={readAll} onChange={(e) => setReadAll(e.target.checked)} className="mt-1 w-4 h-4 accent-[#FFC107]" />
            <div>
              <div className="text-sm font-medium">I have read the full agreement above and I accept all clauses</div>
              <div className="text-xs text-slate-500 mt-0.5">Including Australian jurisdiction, IP assignment, independent contractor status, India tax responsibility, and confidentiality.</div>
            </div>
          </label>

          <div className="mb-4">
            <label className="text-xs text-slate-600 font-mono uppercase tracking-wider">Type your full legal name exactly as shown in the agreement</label>
            <div className="mt-1 text-xs text-slate-500">Must match: <span className="font-mono font-semibold text-[#0F1E38]">{data.candidate_name}</span></div>
            <Input data-testid="typed-name" value={typedName} onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full name here" className="mt-2 h-14 text-2xl italic font-serif" style={{ fontFamily: "Georgia, serif" }} />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded mb-4 text-xs text-amber-900 flex items-start gap-2">
            <ShieldCheck size={14} className="text-amber-700 flex-shrink-0 mt-0.5" weight="bold" />
            <div>
              By clicking "Sign & Execute Agreement", you consent to your typed name, IP address, browser fingerprint, and the current timestamp being recorded as your legal digital signature under Section 10 of the Australian Electronic Transactions Act 1999.
            </div>
          </div>

          <Button data-testid="sign-btn" onClick={sign} disabled={saving || !typedName.trim() || !readAll}
            className="w-full h-12 bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold text-base">
            {saving ? <><CircleNotch size={16} className="animate-spin mr-2" /> Recording signature…</> : <>Sign &amp; Execute Agreement</>}
          </Button>
        </div>

        <div className="mt-6 text-center text-[10px] text-slate-400 font-mono uppercase tracking-wider">
          Secure digital signing · SoverenX Systems
        </div>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center text-xs font-mono uppercase tracking-wider text-slate-500">{children}</div>;
}
function ScreenMsg({ icon, title, body }) {
  const Icon = icon === "ok" ? CheckCircle : Warning;
  const color = icon === "ok" ? "text-emerald-600" : "text-amber-600";
  const bg = icon === "ok" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200";
  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 shadow-sm text-center">
        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${bg} border`}>
          <Icon size={22} className={color} weight="fill" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-[#0F1E38]">{title}</h2>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">{body}</p>
        <div className="mt-6 pt-4 border-t border-slate-100 text-[10px] text-slate-400 font-mono uppercase tracking-wider">SoverenX Systems</div>
      </div>
    </div>
  );
}
